/**
 * API Configuration
 * Centralized configuration for API endpoints and base URLs
 * 
 * Features:
 * - Environment-based URL configuration
 * - Automatic protocol detection and addition
 * - Input validation and error handling
 * - Production-ready with proper fallbacks
 */

// Constants
const CONFIG = {
  TIMEOUT: 30000,
  DEFAULT_PROTOCOL: 'https://',
  ALLOWED_PROTOCOLS: ['http://', 'https://'],
};

const ERROR_MESSAGES = {
  INVALID_ENDPOINT: 'Invalid endpoint provided',
  INVALID_BASE_URL: 'Invalid base URL configuration',
  MISSING_PROTOCOL: 'Base URL missing protocol',
  MISSING_ENV_VAR: 'REACT_APP_API_BASE environment variable is not set',
};

/**
 * Get API base URL from environment variable
 * @returns {string} The API base URL
 * @throws {Error} If REACT_APP_API_BASE is not set
 */
const getApiBaseUrl = () => {
  console.log('🔍 Environment Debug:', {
    REACT_APP_API_BASE: process.env.REACT_APP_API_BASE,
    NODE_ENV: process.env.NODE_ENV,
    hasApiBase: !!process.env.REACT_APP_API_BASE
  });
  
  if (!process.env.REACT_APP_API_BASE) {
    throw new Error(ERROR_MESSAGES.MISSING_ENV_VAR);
  }
  
  return process.env.REACT_APP_API_BASE;
};

/**
 * Validate and normalize endpoint
 * @param {string} endpoint - The API endpoint to validate
 * @returns {string} Normalized endpoint
 * @throws {Error} If endpoint is invalid
 */
const validateAndNormalizeEndpoint = (endpoint) => {
  if (!endpoint || typeof endpoint !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_ENDPOINT);
  }
  
  // Ensure endpoint starts with '/'
  return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
};

/**
 * Add protocol to base URL if missing
 * @param {string} baseUrl - The base URL to process
 * @returns {string} Base URL with protocol
 */
const ensureProtocol = (baseUrl) => {
  if (!baseUrl) {
    throw new Error(ERROR_MESSAGES.INVALID_BASE_URL);
  }
  
  // Check if URL already has a protocol
  const hasProtocol = CONFIG.ALLOWED_PROTOCOLS.some(protocol => 
    baseUrl.startsWith(protocol)
  );
  
  if (!hasProtocol) {
    return `${CONFIG.DEFAULT_PROTOCOL}${baseUrl}`;
  }
  
  return baseUrl;
};

// Export the API configuration
export const apiConfig = {
  baseUrl: getApiBaseUrl(),
  timeout: CONFIG.TIMEOUT,
  protocol: CONFIG.DEFAULT_PROTOCOL,
};

/**
 * Build full API URL from endpoint
 * @param {string} endpoint - The API endpoint
 * @returns {string} The complete API URL
 * @throws {Error} If endpoint is invalid or URL construction fails
 */
export const buildApiUrl = (endpoint) => {
  try {
    // Get base URL dynamically each time
    let baseUrl = getApiBaseUrl();
    console.log('🔍 buildApiUrl Debug:', { endpoint, originalBaseUrl: baseUrl });
    
    // Validate and normalize endpoint
    const cleanEndpoint = validateAndNormalizeEndpoint(endpoint);
    
    // Ensure the base URL has a protocol
    baseUrl = ensureProtocol(baseUrl);
    console.log('🔍 buildApiUrl Debug:', { cleanEndpoint, finalBaseUrl: baseUrl });
    
    const finalUrl = `${baseUrl}${cleanEndpoint}`;
    console.log('🔍 buildApiUrl Debug - Final URL:', finalUrl);
    
    return finalUrl;
  } catch (error) {
    console.error('Error building API URL:', error.message);
    throw error;
  }
};

// Common API endpoints
export const API_ENDPOINTS = {
  // Authentication
  SIGN_IN: '/auth/sign-in',
  SIGN_UP: '/auth/sign-up',
  REFRESH_TOKEN: '/auth/refresh-token',
  LOGIN_REQUEST: '/auth/login-request',
  RESET_PASSWORD: '/auth/reset-password',
  
  // Templates
  TEMPLATES: '/api/templates',
  BRANCH_TEMPLATES: '/api/templates/branch-templates',
  RENDER_TEMPLATE_HTML: '/api/templates/render-template-html',
  CONVERT_JSON_TO_HTML: '/api/templates/convert-json-to-html',
  GET_TEMPLATE_HTML: '/api/templates/get-template-html',
  TEMPLATE_FOLDERS: '/api/templates/template-folders',
  TEMPLATE_BY_ID: '/api/templates/template/:templateId',
  DEBUG_TEMPLATE: '/api/templates/template/:templateId/debug',
  GENERATE_DOCUMENT: '/api/templates/generate-document',
  
  // Clients
  CLIENTS: '/api/clients',
  
  // Users
  USERS: '/api/users',
  
  // Advisers
  BRANCH_ADVISERS: '/api/advisers/branch',
  ADVISER_DETAILS: '/api/advisers/details',
};

/**
 * Get all API URLs as an object
 * @returns {Object} Object containing all API URLs
 */
export const getApiUrls = () => {
  return Object.entries(API_ENDPOINTS).reduce((urls, [key, endpoint]) => {
    try {
      urls[key] = buildApiUrl(endpoint);
    } catch (error) {
      console.error(`Error building URL for ${key}:`, error.message);
      urls[key] = null;
    }
    return urls;
  }, {});
};

/**
 * Validate API configuration
 * @returns {boolean} True if configuration is valid
 */
export const validateApiConfig = () => {
  try {
    const baseUrl = getApiBaseUrl();
    const testUrl = buildApiUrl('/test');
    return Boolean(baseUrl && testUrl);
  } catch (error) {
    console.error('API configuration validation failed:', error.message);
    return false;
  }
};

export default apiConfig;
